import { useQuery } from "@tanstack/react-query";

import { ACTIVE_CHAIN } from "@/config/chain";
import { ERC4626_ABI, SUPPLY_VAULT } from "@/config/vault";
import { buildPublicClient } from "@/lib/wallet/clients";

export type VaultStats = {
  /** Net supply APY (decimal, after fees + rewards). null if unavailable. */
  readonly netApy: number | null;
  /** Total assets supplied to the vault, raw (USDC 1e6). */
  readonly totalAssetsRaw: bigint | null;
};

const MORPHO_API = "https://blue-api.morpho.org/graphql";

const VAULT_QUERY = `query Vault($address: String!, $chainId: Int!) {
  vaultByAddress(address: $address, chainId: $chainId) {
    state { apy netApy }
  }
}`;

/**
 * Vault-level stats. Total assets come straight from the 4626 contract; the APY
 * is sourced from Morpho's public API since it depends on the curator's market
 * allocation and isn't cheaply derivable on-chain. If the API is unreachable
 * the page still renders — APY just shows as unavailable.
 */
export const useVault = () =>
  useQuery({
    queryKey: ["vault-stats", SUPPLY_VAULT.address],
    queryFn: async (): Promise<VaultStats> => {
      const client = buildPublicClient();
      const totalAssetsPromise = client.readContract({
        address: SUPPLY_VAULT.address,
        abi: ERC4626_ABI,
        functionName: "totalAssets",
      });

      let netApy: number | null = null;
      try {
        const res = await fetch(MORPHO_API, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            query: VAULT_QUERY,
            variables: {
              address: SUPPLY_VAULT.address.toLowerCase(),
              chainId: ACTIVE_CHAIN.id,
            },
          }),
        });
        const json = await res.json();
        const state = json?.data?.vaultByAddress?.state;
        const raw = state?.netApy ?? state?.apy;
        if (typeof raw === "number") netApy = raw;
      } catch {
        // Leave netApy null — the page renders without the APY figure.
      }

      return { netApy, totalAssetsRaw: await totalAssetsPromise };
    },
    staleTime: 60_000,
  });
